/**
 * Prospecting — data-source-agnostic page for pulling contacts/companies into
 * Acquilix. Apollo.io is the first configured source; add more by mirroring
 * the tabs pattern here + a route in routes_iter43.py.
 *
 * Tabs: Search · Prospects · Settings
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import CallButton from "@/components/CallButton";
import QuickContactActions from "@/components/QuickContactActions";
import AdvancedTable from "@/components/table/AdvancedTable";
import ResearchDialog from "@/components/ResearchDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Search as SearchIcon, Users, Building2, Settings as SettingsIcon,
  KeyRound, Save, Trash2, ArrowRight, Sparkles, Loader2, ExternalLink, Download,
  AlertCircle, CheckCircle2, MapPin, Briefcase, Wand2, Tag as TagIcon, Zap,
} from "lucide-react";
import { toast } from "sonner";
import SearchableSelect from "@/components/ui/SearchableSelect";
import useApolloBulkEnrich from "@/lib/useApolloBulkEnrich";
import { apiErrorMessage } from "@/lib/apiError";


const SENIORITIES = ["c_suite", "founder", "owner", "partner", "vp", "head", "director", "manager", "senior", "entry", "intern"];
const EMP_RANGES = ["1,10", "11,20", "21,50", "51,200", "201,500", "501,1000", "1001,5000", "5001,10000", "10001+"];

export default function Prospecting() {
  const { activeWorkspaceId } = useAuth();
  const [tab, setTab] = useState("search");
  return (
    <div className="space-y-6" data-testid="prospecting-page">
      <PageHeader title="Prospecting" subtitle="Pull people and companies from Apollo.io and other sources into Acquilix for WA and email marketing." icon={SearchIcon} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="search" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="prospecting-tab-search"><SearchIcon className="w-4 h-4 mr-2" />Search</TabsTrigger>
          <TabsTrigger value="prospects" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="prospecting-tab-prospects"><Users className="w-4 h-4 mr-2" />Prospects</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="prospecting-tab-settings"><SettingsIcon className="w-4 h-4 mr-2" />Sources</TabsTrigger>
        </TabsList>
        <TabsContent value="search" forceMount className={`mt-6 ${tab === "search" ? "" : "hidden"}`}><SearchTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="prospects" className="mt-6"><ProspectsTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SourcesTab wsId={activeWorkspaceId} /></TabsContent>
      </Tabs>
    </div>
  );
}


// ─────────────── Search tab ───────────────

const SOURCE_PILLS = [
  { key: "apollo", label: "Apollo.io" },
  { key: "web", label: "AI Web Search" },
  { key: "linkedin_salesnav", label: "LinkedIn" },
  { key: "zoominfo", label: "ZoomInfo" },
  { key: "clearbit", label: "Clearbit" },
];

function SearchTab({ wsId }) {
  const [source, setSource] = useState("apollo");
  const [kind, setKind] = useState("people");
  const [status, setStatus] = useState(null);
  const [sources, setSources] = useState([]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/integrations/apollo`).then((r) => setStatus(r.data));
    api.get(`/workspaces/${wsId}/prospect-sources`).then((r) => setSources(r.data)).catch(() => {});
  }, [wsId]);

  const configured = (k) => (sources.find((s) => s.source === k) || {}).configured;
  const pills = (
    <div className="flex items-center gap-1 flex-wrap mb-4" data-testid="source-pills">
      {SOURCE_PILLS.map((s) => (
        <button key={s.key} onClick={() => setSource(s.key)}
          className={`text-xs font-medium px-3 py-1.5 border ${source === s.key ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground/70 hover:border-foreground/40"}`}
          data-testid={`source-pill-${s.key}`}>
          {s.label}{sources.length > 0 && !configured(s.key) && <span className="ml-1 text-[9px] opacity-60">⚿</span>}
        </button>
      ))}
    </div>
  );

  if (!status) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading…</CardContent></Card>;
  if (source === "web") return <>{pills}<WebSearchForm wsId={wsId} /></>;
  if (source !== "apollo") return <>{pills}<ProviderSearchForm wsId={wsId} source={source} configured={!!configured(source)} /></>;
  if (!status.configured) {
    return (
      <>{pills}
      <Card className="rounded-none border-amber-500/50" data-testid="apollo-not-configured">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5 text-amber-600" /><h3 className="font-bold">Apollo API key not configured</h3></div>
          <p className="text-sm text-foreground/70">Head to the <strong>Sources</strong> tab and paste your workspace-specific Apollo.io API key. Keys are stored encrypted at rest and never returned. Meanwhile, try the keyless <strong>AI Web Search</strong> source above.</p>
        </CardContent>
      </Card>
      </>
    );
  }
  return (
    <div data-testid="prospecting-search">
      {pills}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        <SearchForm wsId={wsId} kind={kind} setKind={setKind} source={source} setSource={setSource} />
      </div>
    </div>
  );
}

function SearchForm({ wsId, kind, setKind }) {
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [searchError, setSearchError] = useState(null); // R52 · Apollo scope errors
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [catalogs, setCatalogs] = useState(null);
  const [advOpen, setAdvOpen] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [loadedSearchId, setLoadedSearchId] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [exporting, setExporting] = useState(false);

  // Load catalogs + saved searches once
  useEffect(() => {
    api.get("/apollo/catalogs").then((r) => setCatalogs(r.data)).catch(() => setCatalogs({}));
  }, []);
  const loadSaved = () => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/prospecting/saved-searches`)
      .then((r) => setSavedSearches(r.data.rows || []))
      .catch(() => setSavedSearches([]));
  };
  useEffect(loadSaved, [wsId]);

  // Shared
  const [qKeywords, setQKeywords] = useState("");
  // People-only
  const [titles, setTitles] = useState("");
  const [notTitles, setNotTitles] = useState("");
  const [pastTitles, setPastTitles] = useState("");
  const [seniorities, setSeniorities] = useState([]);
  const [personLocs, setPersonLocs] = useState("");
  const [personName, setPersonName] = useState("");
  const [depIds, setDepIds] = useState([]);
  const [funcs, setFuncs] = useState([]);
  const [emailStatus, setEmailStatus] = useState([]);
  // Company / shared org
  const [orgLocs, setOrgLocs] = useState("");
  const [notOrgLocs, setNotOrgLocs] = useState("");
  const [industryIds, setIndustryIds] = useState([]);         // multi
  const [empRanges, setEmpRanges] = useState([]);
  const [revenueRange, setRevenueRange] = useState("");       // single "min,max"
  const [orgKeywords, setOrgKeywords] = useState("");
  const [orgName, setOrgName] = useState("");
  const [orgDomains, setOrgDomains] = useState("");
  const [tech, setTech] = useState("");
  const [fundingStages, setFundingStages] = useState([]);

  const csv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  const parseRevenue = (v) => {
    if (!v) return null;
    const [min, max] = v.split(",");
    return { min: min ? Number(min) : 0, max: max ? Number(max) : 0 };
  };

  const industriesOpts = useMemo(
    () => (catalogs?.industries || []).map((i) => ({ value: i.id, label: i.name })),
    [catalogs]);
  const seniorityOpts = useMemo(
    () => (catalogs?.seniorities || []).map((i) => ({ value: i.id, label: i.name })),
    [catalogs]);
  const departmentOpts = useMemo(
    () => (catalogs?.departments || []).map((i) => ({ value: i.id, label: i.name })),
    [catalogs]);
  const functionOpts = useMemo(
    () => (catalogs?.functions || []).map((f) => ({ value: f,
      label: f.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) })),
    [catalogs]);
  const revenueOpts = catalogs?.revenue_ranges || [];
  const fundingOpts = catalogs?.funding_stages || [];
  const empRangeOpts = catalogs?.employee_ranges || EMP_RANGES;
  const emailStatusOpts = catalogs?.email_status || [];

  const run = async (nextPage = 1) => {
    setBusy(true);
    setSelected({});
    try {
      const revRange = parseRevenue(revenueRange);
      const payload = kind === "people" ? {
        q_keywords: qKeywords || undefined,
        q_person_name: personName || undefined,
        person_titles: csv(titles),
        person_not_titles: csv(notTitles),
        person_past_titles: csv(pastTitles),
        person_seniorities: seniorities,
        person_locations: csv(personLocs),
        organization_locations: csv(orgLocs),
        person_departments: depIds,
        person_functions: funcs,
        organization_industry_tag_ids: industryIds,
        organization_num_employees_ranges: empRanges,
        q_organization_name: orgName || undefined,
        q_organization_domains: orgDomains || undefined,
        currently_using_any_of_technology_uids: csv(tech),
        revenue_range: revRange || undefined,
        organization_latest_funding_stage_cd: fundingStages,
        contact_email_status_v2: emailStatus,
        page: nextPage, per_page: perPage,
      } : {
        q_organization_keyword_tags: csv(qKeywords),
        q_organization_name: orgName || undefined,
        q_organization_domains: orgDomains || undefined,
        organization_locations: csv(orgLocs),
        organization_not_locations: csv(notOrgLocs),
        organization_industry_tag_ids: industryIds,
        organization_num_employees_ranges: empRanges,
        revenue_range: revRange || undefined,
        currently_using_any_of_technology_uids: csv(tech),
        organization_latest_funding_stage_cd: fundingStages,
        page: nextPage, per_page: perPage,
      };
      const url = `/workspaces/${wsId}/apollo/search/${kind === "people" ? "people" : "companies"}`;
      const r = await api.post(url, payload);
      setResults(r.data);
      setPage(nextPage);
    } catch (e) {
      // R52 · Rich error surface for Apollo scope failures. The backend
      // now returns `{code: 'apollo_key_scope', message, hint, endpoint_blocked}`
      // on 403 API_INACCESSIBLE — render an actionable red card + toast
      // rather than a wall of raw Apollo JSON.
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "apollo_key_scope") {
        setSearchError({ ...d });
        toast.error(`Apollo key scope issue · ${d.endpoint_blocked}`,
          { description: d.hint, duration: 12000 });
      } else {
        setSearchError(null);
        toast.error(typeof d === "string" ? d : (d?.message || "Apollo search failed"));
      }
      setResults(null);
    } finally { setBusy(false); }
  };

  const importSelected = async () => {
    if (!results?.items?.length) return;
    const picks = results.items.filter((_, i) => selected[i]);
    if (picks.length === 0) { toast.error("Select at least one row"); return; }
    setImporting(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/prospects/import`, {
        kind: kind === "people" ? "person" : "company",
        records: picks,
      });
      toast.success(`Imported ${r.data.imported} · Updated ${r.data.updated}`);
      setSelected({});
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setImporting(false); }
  };

  // R69d · Bulk-enrich selected people in the search results (email + phone
  // reveal via Apollo /people/bulk_match, batched 10 at a time). Only
  // meaningful for `kind==='people'`. Rewrites the corresponding rows in
  // `results.items` so the table reflects revealed fields immediately.
  const { run: runSearchBulkEnrich, loading: bulkEnriching, progress: bulkEnrichProgress } = useApolloBulkEnrich(wsId);
  const bulkEnrichSelected = async () => {
    if (kind !== "people") { toast.error("Bulk enrich is available for People searches"); return; }
    if (!results?.items?.length) return;
    const idxList = results.items.map((_, i) => i).filter((i) => selected[i]);
    if (idxList.length === 0) { toast.error("Select at least one person"); return; }
    const items = idxList.map((i) => {
      const p = results.items[i];
      return {
        apollo_id: p.apollo_id || p.id || null,
        email: p.email || null,
        linkedin_url: p.linkedin_url || null,
        first_name: p.first_name || null,
        last_name: p.last_name || null,
        organization_name: (p.organization && p.organization.name) || p.company_name || null,
      };
    });
    const data = await runSearchBulkEnrich(items);
    if (!data) return;
    // Merge enrichment back into the visible rows.
    const newItems = [...results.items];
    (data.results || []).forEach((res, j) => {
      if (res && res.status === "enriched" && res.person) {
        const orig = newItems[idxList[j]] || {};
        newItems[idxList[j]] = { ...orig, ...res.person };
      }
    });
    setResults({ ...results, items: newItems });
  };

  const clearAll = () => {
    setQKeywords(""); setTitles(""); setNotTitles(""); setPastTitles(""); setSeniorities([]);
    setPersonLocs(""); setPersonName(""); setDepIds([]); setFuncs([]); setEmailStatus([]);
    setOrgLocs(""); setNotOrgLocs(""); setIndustryIds([]); setEmpRanges([]); setRevenueRange("");
    setOrgKeywords(""); setOrgName(""); setOrgDomains(""); setTech(""); setFundingStages([]);
    setResults(null);
    setLoadedSearchId(null);
  };

  // ─── Saved-search filter capture + apply ─────────────────────────
  const captureFilters = () => ({
    qKeywords, titles, notTitles, pastTitles, seniorities,
    personLocs, personName, depIds, funcs, emailStatus,
    orgLocs, notOrgLocs, industryIds, empRanges, revenueRange,
    orgKeywords, orgName, orgDomains, tech, fundingStages,
  });
  const applyFilters = (f) => {
    setQKeywords(f.qKeywords || ""); setTitles(f.titles || "");
    setNotTitles(f.notTitles || ""); setPastTitles(f.pastTitles || "");
    setSeniorities(f.seniorities || []); setPersonLocs(f.personLocs || "");
    setPersonName(f.personName || ""); setDepIds(f.depIds || []);
    setFuncs(f.funcs || []); setEmailStatus(f.emailStatus || []);
    setOrgLocs(f.orgLocs || ""); setNotOrgLocs(f.notOrgLocs || "");
    setIndustryIds(f.industryIds || []); setEmpRanges(f.empRanges || []);
    setRevenueRange(f.revenueRange || ""); setOrgKeywords(f.orgKeywords || "");
    setOrgName(f.orgName || ""); setOrgDomains(f.orgDomains || "");
    setTech(f.tech || ""); setFundingStages(f.fundingStages || []);
  };
  const saveCurrent = async () => {
    if (!saveName.trim()) { toast.error("Give this search a name"); return; }
    try {
      await api.post(`/workspaces/${wsId}/prospecting/saved-searches`, {
        name: saveName.trim(),
        kind,
        filters: captureFilters(),
      });
      toast.success(`Saved · "${saveName.trim()}"`);
      setShowSaveDialog(false); setSaveName("");
      loadSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    }
  };
  const runSaved = async (s) => {
    setKind(s.kind);
    applyFilters(s.filters || {});
    setLoadedSearchId(s.id);
    try { api.post(`/workspaces/${wsId}/prospecting/saved-searches/${s.id}/touch`); } catch { /* ignore */ }
    setTimeout(() => run(1), 60);   // let state settle before firing search
  };
  const deleteSaved = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/workspaces/${wsId}/prospecting/saved-searches/${s.id}`);
      toast.success("Deleted");
      loadSaved();
      if (loadedSearchId === s.id) setLoadedSearchId(null);
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // ─── Excel export of current search results ──────────────────────
  const exportXlsx = async () => {
    if (!results?.items?.length) { toast.error("Run a search first"); return; }
    setExporting(true);
    try {
      const resp = await api.post(
        `/workspaces/${wsId}/prospecting/export/xlsx`,
        { kind: kind === "people" ? "people" : "companies",
          records: results.items,
          filename: `apollo-${kind}-${new Date().toISOString().slice(0, 10)}` },
        { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `apollo-${kind}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      toast.success(`Exported ${results.items.length} rows`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Export failed"));
    } finally { setExporting(false); }
  };

  const activeFilterCount = [
    qKeywords, titles, notTitles, pastTitles, personLocs, personName, orgLocs, notOrgLocs,
    orgName, orgDomains, tech, revenueRange,
  ].filter((x) => x && x.length > 0).length
    + seniorities.length + depIds.length + funcs.length + emailStatus.length
    + industryIds.length + empRanges.length + fundingStages.length;

  return (
    <>
      <Card className="rounded-none h-fit sticky top-4">
        <CardContent className="p-4 space-y-3">
          {savedSearches.length > 0 && (
            <div className="border-b border-border pb-3" data-testid="apollo-saved-searches">
              <div className="label-eyebrow mb-1.5 flex items-center justify-between">
                <span>⭐ Saved searches ({savedSearches.length})</span>
              </div>
              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                {savedSearches.slice(0, 12).map((s) => (
                  <div
                    key={s.id}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] border ${loadedSearchId === s.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"} cursor-pointer`}
                    data-testid={`apollo-saved-${s.id}`}
                    title={s.description || `${s.kind} · ${s.use_count || 0} runs`}
                  >
                    <button
                      type="button"
                      onClick={() => runSaved(s)}
                      className="flex-1 text-left truncate max-w-[130px]"
                      data-testid={`apollo-saved-run-${s.id}`}
                    >
                      {s.kind === "people" ? "👥" : "🏢"} {s.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSaved(s)}
                      className="text-muted-foreground hover:text-red-600"
                      data-testid={`apollo-saved-delete-${s.id}`}
                      aria-label="Delete"
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-1 border border-border p-0.5 w-full">
            <button className={`flex-1 text-xs font-medium py-1.5 ${kind === "people" ? "bg-primary text-primary-foreground" : "text-foreground/70"}`} onClick={() => setKind("people")} data-testid="apollo-kind-people">People</button>
            <button className={`flex-1 text-xs font-medium py-1.5 ${kind === "companies" ? "bg-primary text-primary-foreground" : "text-foreground/70"}`} onClick={() => setKind("companies")} data-testid="apollo-kind-companies">Companies</button>
          </div>

          {/* Always-visible primary filters */}
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Keywords</Label>
            <Input value={qKeywords} onChange={(e) => setQKeywords(e.target.value)} placeholder={kind === "people" ? "SaaS, healthcare…" : "cybersecurity, martech, …"} className="h-9 rounded-none" data-testid="apollo-keywords" />
          </div>

          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Industry (Apollo catalog)</Label>
            <MultiSearchableSelect
              value={industryIds}
              onChange={setIndustryIds}
              options={industriesOpts}
              placeholder="+ Add industry"
              testid="apollo-industry"
            />
          </div>

          {kind === "people" ? (
            <>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Titles</Label>
                <Input value={titles} onChange={(e) => setTitles(e.target.value)} placeholder="CEO, Founder, VP Sales" className="h-9 rounded-none" data-testid="apollo-titles" />
                <div className="text-[10px] text-muted-foreground mt-1">Comma-separated. Include similar titles automatically.</div>
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Seniorities</Label>
                <MultiSearchableSelect
                  value={seniorities} onChange={setSeniorities}
                  options={seniorityOpts} placeholder="+ Any seniority"
                  testid="apollo-seniorities"
                />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Person locations</Label>
                <Input value={personLocs} onChange={(e) => setPersonLocs(e.target.value)} placeholder="Bengaluru, San Francisco" className="h-9 rounded-none" data-testid="apollo-person-locs" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Company HQ locations</Label>
                <Input value={orgLocs} onChange={(e) => setOrgLocs(e.target.value)} placeholder="India, United States" className="h-9 rounded-none" data-testid="apollo-org-locs" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Employee ranges</Label>
                <MultiSearchableSelect
                  value={empRanges} onChange={setEmpRanges}
                  options={empRangeOpts.map((e) => ({ value: e,
                    label: e.replace(",", " – ").replace(/,$/, "+") }))}
                  placeholder="+ Any size"
                  testid="apollo-emp-ranges"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Company HQ locations</Label>
                <Input value={orgLocs} onChange={(e) => setOrgLocs(e.target.value)} placeholder="India, EMEA" className="h-9 rounded-none" data-testid="apollo-org-locs" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Employee ranges</Label>
                <MultiSearchableSelect
                  value={empRanges} onChange={setEmpRanges}
                  options={empRangeOpts.map((e) => ({ value: e,
                    label: e.replace(",", " – ").replace(/,$/, "+") }))}
                  placeholder="+ Any size"
                  testid="apollo-emp-ranges"
                />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Revenue</Label>
                <select
                  value={revenueRange}
                  onChange={(e) => setRevenueRange(e.target.value)}
                  className="h-9 w-full text-xs border border-border bg-background px-2 rounded-none"
                  data-testid="apollo-revenue"
                >
                  <option value="">Any revenue</option>
                  {revenueOpts.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Advanced (collapsed by default) */}
          <button
            type="button"
            onClick={() => setAdvOpen((v) => !v)}
            className="text-[10px] uppercase tracking-widest text-primary hover:underline font-semibold"
            data-testid="apollo-adv-toggle"
          >
            {advOpen ? "▲ Hide" : "▼ Show"} advanced filters
          </button>

          {advOpen && (
            <div className="space-y-3 border-t border-border pt-3" data-testid="apollo-adv-panel">
              {kind === "people" && (
                <>
                  <div>
                    <Label className="label-eyebrow mb-1.5 inline-block">Person name</Label>
                    <Input value={personName} onChange={(e) => setPersonName(e.target.value)} placeholder="John Smith" className="h-9 rounded-none" data-testid="apollo-person-name" />
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-1.5 inline-block">Exclude titles</Label>
                    <Input value={notTitles} onChange={(e) => setNotTitles(e.target.value)} placeholder="Intern, Assistant" className="h-9 rounded-none" data-testid="apollo-not-titles" />
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-1.5 inline-block">Past titles</Label>
                    <Input value={pastTitles} onChange={(e) => setPastTitles(e.target.value)} placeholder="Founder, CEO" className="h-9 rounded-none" data-testid="apollo-past-titles" />
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-1.5 inline-block">Departments</Label>
                    <MultiSearchableSelect
                      value={depIds} onChange={setDepIds}
                      options={departmentOpts} placeholder="+ Any department"
                      testid="apollo-departments"
                    />
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-1.5 inline-block">Functions</Label>
                    <MultiSearchableSelect
                      value={funcs} onChange={setFuncs}
                      options={functionOpts} placeholder="+ Any function"
                      testid="apollo-functions"
                    />
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-1.5 inline-block">Email status</Label>
                    <MultiSearchableSelect
                      value={emailStatus} onChange={setEmailStatus}
                      options={emailStatusOpts.map((s) => ({ value: s.id, label: s.name }))}
                      placeholder="+ Any status"
                      testid="apollo-email-status"
                    />
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-1.5 inline-block">Revenue</Label>
                    <select
                      value={revenueRange}
                      onChange={(e) => setRevenueRange(e.target.value)}
                      className="h-9 w-full text-xs border border-border bg-background px-2 rounded-none"
                      data-testid="apollo-revenue"
                    >
                      <option value="">Any revenue</option>
                      {revenueOpts.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {kind === "companies" && (
                <div>
                  <Label className="label-eyebrow mb-1.5 inline-block">Exclude HQ locations</Label>
                  <Input value={notOrgLocs} onChange={(e) => setNotOrgLocs(e.target.value)} placeholder="China, Russia" className="h-9 rounded-none" data-testid="apollo-not-org-locs" />
                </div>
              )}
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Company name contains</Label>
                <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Acme" className="h-9 rounded-none" data-testid="apollo-org-name" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Company domains</Label>
                <Input value={orgDomains} onChange={(e) => setOrgDomains(e.target.value)} placeholder="acme.com, example.io" className="h-9 rounded-none" data-testid="apollo-org-domains" />
                <div className="text-[10px] text-muted-foreground mt-1">Comma-separated.</div>
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Technologies used</Label>
                <Input value={tech} onChange={(e) => setTech(e.target.value)} placeholder="salesforce, hubspot" className="h-9 rounded-none" data-testid="apollo-tech" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Latest funding stage</Label>
                <MultiSearchableSelect
                  value={fundingStages} onChange={setFundingStages}
                  options={fundingOpts.map((f) => ({ value: f.id, label: f.name }))}
                  placeholder="+ Any stage"
                  testid="apollo-funding-stages"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-1">
            <Button className="flex-1 rounded-none" onClick={() => run(1)} disabled={busy} data-testid="apollo-run">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching…</> : <><SearchIcon className="w-4 h-4 mr-2" />Run search</>}
            </Button>
            <Button
              variant="outline" size="sm" className="rounded-none"
              onClick={() => setShowSaveDialog(true)}
              disabled={activeFilterCount === 0}
              data-testid="apollo-save"
              title="Save this filter combo"
            >
              <Save className="w-3.5 h-3.5" />
            </Button>
            {activeFilterCount > 0 && (
              <Button variant="outline" size="sm" className="rounded-none" onClick={clearAll} data-testid="apollo-clear" title="Clear all filters">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          {activeFilterCount > 0 && (
            <div className="text-[10px] text-muted-foreground" data-testid="apollo-filter-count">
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} active
            </div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-3 min-w-0">
        {searchError && (
          <Card className="rounded-none border-rose-500/50 bg-rose-50 dark:bg-rose-950/20" data-testid="apollo-scope-error">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-rose-600" />
                <h3 className="font-bold text-rose-800">Apollo API key scope issue</h3>
              </div>
              <p className="text-sm text-rose-900">
                Your Apollo API key isn&apos;t authorized to hit{" "}
                <code className="text-[11px] bg-white/70 px-1 py-0.5 border border-rose-300">
                  {searchError.endpoint_blocked}
                </code>. This is enforced by Apollo — not a bug in Acquilix.
              </p>
              <p className="text-xs text-rose-900/80">{searchError.hint}</p>
              <div className="flex gap-2 pt-1">
                <a href="https://apolloapi.com/docs" target="_blank" rel="noopener noreferrer"
                    className="text-xs underline text-rose-700 hover:text-rose-900">
                  Apollo API scope docs ↗
                </a>
                <button className="text-xs underline text-rose-700 hover:text-rose-900"
                         onClick={() => setSearchError(null)}
                         data-testid="apollo-scope-error-dismiss">
                  Dismiss
                </button>
              </div>
            </CardContent>
          </Card>
        )}
        {!results ? (
          <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="apollo-empty">Set your parameters on the left and hit <strong>Run Apollo search</strong>. Results appear here — pick the ones you want and click Import.</CardContent></Card>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm flex-wrap gap-2">
              <span data-testid="apollo-results-count">
                <strong>{results.total_entries?.toLocaleString?.() ?? results.items.length}</strong> total · showing {results.items.length} on page {results.page}
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="apollo-per-page">
                  Per page
                  <select value={perPage}
                           onChange={(e) => { const n = Number(e.target.value); setPerPage(n); setTimeout(() => run(1), 30); }}
                           className="h-7 text-xs border border-border bg-background px-1 ml-1"
                           data-testid="apollo-per-page-select">
                    {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => run(Math.max(1, page - 1))} disabled={busy || page <= 1} data-testid="apollo-prev">Prev</Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => run(page + 1)} disabled={busy || (results.total_pages && page >= results.total_pages)} data-testid="apollo-next">Next</Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={exportXlsx}
                         disabled={exporting || !results.items?.length}
                         data-testid="apollo-export-xlsx">
                  {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3 mr-1" />Excel</>}
                </Button>
                {kind === "people" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-none border-primary/50 text-primary hover:bg-primary/10"
                    onClick={bulkEnrichSelected}
                    disabled={bulkEnriching || Object.values(selected).filter(Boolean).length === 0}
                    data-testid="apollo-bulk-enrich-btn"
                    title="Reveal email + phone for selected people via Apollo bulk_match"
                  >
                    {bulkEnriching ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                    Enrich {Object.values(selected).filter(Boolean).length || ""}
                  </Button>
                )}
                <Button size="sm" className="rounded-none" onClick={importSelected} disabled={importing || Object.values(selected).filter(Boolean).length === 0} data-testid="apollo-import-btn">
                  <ArrowRight className="w-3 h-3 mr-1" />Import {Object.values(selected).filter(Boolean).length || ""}
                </Button>
              </div>
            </div>
            <div className="border border-border overflow-auto max-h-[75vh] bg-card" data-testid="apollo-results">
              {/* R69e · Progress bar for large bulk-enrich runs */}
              {bulkEnrichProgress && bulkEnrichProgress.total > 10 && (
                <div className="border-b border-border bg-muted/30 px-3 py-2" data-testid="apollo-bulk-enrich-progress">
                  <div className="flex items-center justify-between text-[11px] mb-1">
                    <span className="font-medium">Enriching {bulkEnrichProgress.current}/{bulkEnrichProgress.total} · Apollo bulk_match</span>
                    <span className="text-muted-foreground">
                      {bulkEnrichProgress.emails_revealed} emails · {bulkEnrichProgress.phones_revealed} phones · {bulkEnrichProgress.not_found} not found
                    </span>
                  </div>
                  <div className="h-1.5 bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${(bulkEnrichProgress.current / bulkEnrichProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              {results.items.length === 0
                ? (
                  <div className="p-6 text-sm text-foreground/55 text-center space-y-2" data-testid="apollo-empty">
                    <div>No matches. Widen your criteria.</div>
                    {results.empty_reason && <div className="text-[11px] text-amber-700">{results.empty_reason}</div>}
                    {results.debug_request && Object.keys(results.debug_request).length > 0 && (
                      <div className="text-[10px] text-muted-foreground border p-2 mx-auto max-w-md text-left font-mono">
                        Filters sent to Apollo:<br />{JSON.stringify(results.debug_request, null, 2)}
                      </div>
                    )}
                  </div>
                )
                : kind === "people"
                  ? <PeopleTable rows={results.items} selected={selected}
                                    setSelected={setSelected} wsId={wsId} />
                  : <CompaniesTable rows={results.items} selected={selected}
                                      setSelected={setSelected} />}
            </div>
          </>
        )}
      </div>

      {/* Save-search dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="rounded-none" data-testid="apollo-save-dialog">
          <DialogHeader>
            <DialogTitle>Save this search</DialogTitle>
            <DialogDescription>
              Name your filter combo. Same-named saves overwrite the previous one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. Food manufacturers · India · 51-200 emp · VP+"
              className="rounded-none"
              onKeyDown={(e) => { if (e.key === "Enter") saveCurrent(); }}
              data-testid="apollo-save-name"
              autoFocus
            />
            <div className="text-[10px] text-muted-foreground">
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} · kind: {kind}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveCurrent} data-testid="apollo-save-confirm">
              <Save className="w-3.5 h-3.5 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PersonRow({ p, wsId, onEnriched }) {
  const [busy, setBusy] = useState(false);
  const enrich = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setBusy(true);
    try {
      if (p.id && p.source && p.source !== "apollo") {
        const r = await api.post(`/workspaces/${wsId}/prospects/${p.id}/enrich`);
        if (r.data.revealed?.length) toast.success(`Enriched via ${r.data.via} — ${r.data.revealed.join(", ")}`);
        else toast.info("No new contact info revealed");
        onEnriched?.();
        return;
      }
      const r = await api.post(`/workspaces/${wsId}/apollo/enrich/person`, {
        apollo_id: p.apollo_id || p.source_id,
        email: p.email,
        linkedin_url: p.linkedin_url,
        first_name: p.first_name, last_name: p.last_name,
        organization_name: p.company,
        reveal_personal_emails: true, reveal_phone_number: true,
        prospect_id: p.id && !p.apollo_id ? p.id : undefined,
      });
      // R64.10 · Honest toast — tell user WHAT was revealed, and why not
      // when Apollo held email back. Alt channels get called out so
      // outreach can still happen via LinkedIn / personal email.
      const rs = r.data?._reveal_status || {};
      if (rs.email_revealed || rs.phone_revealed) {
        const bits = [];
        if (r.data.email) bits.push(`✉ ${r.data.email}`);
        if (r.data.phone) bits.push(`☎ ${r.data.phone}`);
        toast.success(`Enriched — ${bits.join(" · ")}`);
      } else if (rs.email_locked_by_apollo_plan) {
        const alt = (rs.alt_channels || [])[0];
        toast.warning(
          "Apollo's returned no email — this contact is behind an Apollo credit paywall on your plan." +
          (alt ? ` Use ${alt.kind}: ${alt.url || alt.value} instead.` : ""),
          { duration: 10000 },
        );
      } else {
        const alt = (rs.alt_channels || [])[0];
        toast.info(
          "No email/phone revealed by Apollo." +
          (alt ? ` Try ${alt.kind}: ${alt.url || alt.value}.` : " Try enriching with LinkedIn URL, or the contact isn't in Apollo's DB."),
          { duration: 10000 },
        );
      }
      onEnriched?.();
    } catch (err) {
      // R64.10 · Surface the actual Apollo error (was hiding details behind "Enrich failed").
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object") {
        toast.error(`${d.message || d.code || "Enrich failed"}${d.hint ? ` — ${d.hint}` : ""}`, { duration: 12000 });
      } else {
        toast.error(String(d || err?.message || "Enrich failed"), { duration: 8000 });
      }
    }
    finally { setBusy(false); }
  };
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-sm">{p.name || "(unnamed)"}</span>
        <QuickContactActions
          entityType="prospect"
          entityId={p.id || p.apollo_id}
          entityName={p.name || p.company}
          phone={p.phone} email={p.email}
        />
        {p.title && <span className="text-xs text-foreground/70">· {p.title}</span>}
        {p.seniority && <Badge variant="outline" className="rounded-none text-[10px]">{p.seniority}</Badge>}
        {p.department && <Badge variant="outline" className="rounded-none text-[10px]">{p.department}</Badge>}
      </div>
      <div className="text-xs text-foreground/60 mt-0.5 flex items-center gap-3 flex-wrap">
        {p.company && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{p.company}</span>}
        {(p.city || p.country) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[p.city, p.country].filter(Boolean).join(", ")}</span>}
        {p.linkedin_url && <a href={p.linkedin_url} target="_blank" rel="noreferrer" className="underline hover:no-underline inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>LinkedIn <ExternalLink className="w-3 h-3" /></a>}
      </div>
      <div className="text-xs mt-1 flex items-center gap-2">
        {p.email
          ? <span className="text-emerald-700 font-mono">{p.email}</span>
          : (
            <>
              <span className="text-amber-700"><AlertCircle className="w-3 h-3 inline mr-1" />{p.email_locked_hint || "Email locked"}</span>
              {wsId && <button onClick={enrich} disabled={busy} className="inline-flex items-center gap-1 text-[11px] underline hover:no-underline text-primary" data-testid={`enrich-${p.apollo_id || p.id}`}>
                <Wand2 className="w-3 h-3" />{busy ? "Enriching…" : "Enrich"}
              </button>}
            </>
          )}
        {p.phone && (
          <>
            <span className="ml-3 font-mono">{p.phone}</span>
            <CallButton phone={p.phone} customer_name={p.name}
              entity_type="prospect" entity_id={p.id || p.apollo_id}
              entity_name={p.name || p.company}
              testid={`prospect-call-${p.id || p.apollo_id}`} />
          </>
        )}
      </div>
    </div>
  );
}

function CompanyRow({ c }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-sm">{c.name || "(unnamed)"}</span>
        {c.industry && <Badge variant="outline" className="rounded-none text-[10px]">{c.industry}</Badge>}
        {c.employees && <span className="text-xs text-foreground/60">· {c.employees} employees</span>}
      </div>
      <div className="text-xs text-foreground/60 mt-0.5 flex items-center gap-3 flex-wrap">
        {c.domain && <span>{c.domain}</span>}
        {(c.city || c.country) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[c.city, c.country].filter(Boolean).join(", ")}</span>}
        {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="underline hover:no-underline inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>Website <ExternalLink className="w-3 h-3" /></a>}
        {c.revenue && <span>· {c.revenue}</span>}
      </div>
      {c.description && <div className="text-xs text-foreground/70 mt-1 line-clamp-2">{c.description}</div>}
    </div>
  );
}

function MultiCheckbox({ items, value, onChange, testid }) {
  const toggle = (v) => {
    const s = new Set(value);
    s.has(v) ? s.delete(v) : s.add(v);
    onChange(Array.from(s));
  };
  return (
    <div className="grid grid-cols-2 gap-1 text-xs" data-testid={testid}>
      {items.map((v) => (
        <label key={v} className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={value.includes(v)} onChange={() => toggle(v)} />
          <span>{v.replace(/_/g, " ")}</span>
        </label>
      ))}
    </div>
  );
}

// Iter 201 · Multi-select using the existing SearchableSelect (single-select)
// under the hood + chip removal — perfect for Apollo industry / seniority /
// department / function / funding-stage / email-status / employee-range pickers.
function MultiSearchableSelect({ value, onChange, options, placeholder,
                                   testid }) {
  const chosen = (value || []).map((v) => {
    const opt = options.find((o) => o.value === v);
    return opt || { value: v, label: v };
  });
  const add = (v) => {
    if (!v || (value || []).includes(v)) return;
    onChange([...(value || []), v]);
  };
  const remove = (v) => onChange((value || []).filter((x) => x !== v));
  const available = options.filter((o) => !(value || []).includes(o.value));
  return (
    <div data-testid={testid}>
      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {chosen.map((c) => (
            <span
              key={c.value}
              className="chip chip-pending text-[10px] inline-flex items-center gap-1 max-w-[200px]"
              data-testid={`${testid}-chip-${c.value}`}
            >
              <span className="truncate" title={c.label}>{c.label}</span>
              <button
                type="button"
                onClick={() => remove(c.value)}
                className="hover:text-red-500"
                data-testid={`${testid}-remove-${c.value}`}
                aria-label={`Remove ${c.label}`}
              >×</button>
            </span>
          ))}
        </div>
      )}
      <SearchableSelect
        value=""
        onValueChange={add}
        options={available}
        placeholder={placeholder || "+ Add…"}
        width="100%"
      />
    </div>
  );
}


// Iter 203 · Rich tabular result views + select-all
function PeopleTable({ rows, selected, setSelected, wsId }) {
  const allChecked = rows.length > 0 && rows.every((_, i) => selected[i]);
  const someChecked = rows.some((_, i) => selected[i]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(rows.reduce((acc, _, i) => { acc[i] = true; return acc; }, {}));
  };
  const clean = (v) => v == null || v === "" ? "—" : v;
  const joinList = (arr) => Array.isArray(arr) && arr.length ? arr.join(", ") : "—";
  const linkOut = (url) => url
    ? <a href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}><ExternalLink className="w-3 h-3 inline" /></a>
    : <span className="text-muted-foreground">—</span>;

  return (
    <table className="text-[11px] w-full border-collapse" data-testid="apollo-people-table">
      <thead className="sticky top-0 z-10 bg-muted/70 backdrop-blur">
        <tr>
          <th className="px-2 py-2 text-left border-b border-border w-8 sticky left-0 bg-muted/70">
            <input type="checkbox" checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                    onChange={toggleAll}
                    data-testid="apollo-select-all" />
          </th>
          {["Name", "Title", "Company", "Industry", "Sub-industry", "Employees", "Email",
            "Status", "Phone", "Location", "Seniority", "Dept", "LinkedIn",
            "Twitter", "Prev role", "Enrich"].map((h) => (
            <th key={h} className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold border-b border-border whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => {
          const loc = [p.city, p.state, p.country].filter(Boolean).join(", ");
          return (
            <tr key={p.apollo_id || i}
                className={`border-b border-border hover:bg-muted/20 cursor-pointer ${selected[i] ? "bg-primary/5" : ""}`}
                onClick={() => setSelected((s) => ({ ...s, [i]: !s[i] }))}
                data-testid={`apollo-row-${i}`}>
              <td className="px-2 py-1.5 sticky left-0 bg-card border-r border-border">
                <input type="checkbox" checked={!!selected[i]} readOnly
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setSelected((s) => ({ ...s, [i]: e.target.checked }))}
                        data-testid={`apollo-check-${i}`} />
              </td>
              <td className="px-2 py-1.5 font-semibold whitespace-nowrap">
                <div className="flex items-center gap-1">
                  {p.photo_url && <img src={p.photo_url} alt="" className="w-5 h-5 rounded-full flex-none" />}
                  <span className="truncate max-w-[160px]" title={p.name}>{clean(p.name)}</span>
                </div>
              </td>
              <td className="px-2 py-1.5 max-w-[180px]">
                <div className="truncate" title={p.title}>{clean(p.title)}</div>
                {p.headline && <div className="text-[9px] text-muted-foreground truncate" title={p.headline}>{p.headline}</div>}
              </td>
              <td className="px-2 py-1.5">
                <div className="truncate max-w-[160px]" title={p.company}>{clean(p.company)}</div>
                {p.company_domain && <div className="text-[9px] text-muted-foreground">{p.company_domain}</div>}
              </td>
              <td className="px-2 py-1.5 whitespace-nowrap max-w-[130px] truncate">{clean(p.company_industry)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap max-w-[130px] truncate text-muted-foreground" title={p.company_sub_industry || (p.company_industries || []).join(", ")}>{clean(p.company_sub_industry)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{clean(p.company_employees)}</td>
              <td className="px-2 py-1.5 max-w-[160px]">
                {p.email
                  ? <a href={`mailto:${p.email}`} className="text-primary hover:underline truncate block" onClick={(e) => e.stopPropagation()}>{p.email}</a>
                  : p.linkedin_url
                    ? <a href={p.linkedin_url} target="_blank" rel="noreferrer"
                         onClick={(e) => e.stopPropagation()}
                         className="text-[10px] text-blue-600 hover:underline truncate block"
                         title="Email locked in Apollo — use LinkedIn to reach out"
                         data-testid={`prospect-linkedin-fallback-${p.apollo_id || i}`}>
                        locked · use LinkedIn ↗
                      </a>
                    : <span className="text-muted-foreground italic">locked</span>}
              </td>
              <td className="px-2 py-1.5 whitespace-nowrap">
                {p.email_status
                  ? <Badge variant="outline" className={`text-[9px] ${p.email_status === "verified" ? "border-emerald-500/50 text-emerald-700" : "border-amber-500/50 text-amber-700"}`}>{p.email_status}</Badge>
                  : "—"}
              </td>
              <td className="px-2 py-1.5 whitespace-nowrap font-mono">{clean(p.phone)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap max-w-[140px] truncate" title={loc}>{clean(loc)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap">{clean(p.seniority)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap max-w-[110px] truncate">{joinList(p.departments)}</td>
              <td className="px-2 py-1.5 text-center">{linkOut(p.linkedin_url)}</td>
              <td className="px-2 py-1.5 text-center">{linkOut(p.twitter_url)}</td>
              <td className="px-2 py-1.5 max-w-[160px] truncate" title={`${p.prev_title || ""} @ ${p.prev_company || ""}`}>
                {p.prev_title ? `${p.prev_title}${p.prev_company ? ` @ ${p.prev_company}` : ""}` : "—"}
              </td>
              <td className="px-2 py-1.5">
                <PersonEnrichCell p={p} wsId={wsId} idx={i} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PersonEnrichCell({ p, wsId, idx }) {
  const [busy, setBusy] = useState(false);
  const [enriched, setEnriched] = useState(null);
  const shown = enriched || p;
  const doEnrich = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/apollo/enrich/person`, {
        apollo_id: p.apollo_id,
        first_name: p.first_name, last_name: p.last_name,
        email: p.email, linkedin_url: p.linkedin_url,
        organization_name: p.company,
        company_domain: p.company_domain, name: p.name,
        reveal_personal_emails: true, reveal_phone_number: true,
      });
      setEnriched(r.data);
      // R64.10 · Honest post-enrich toast — same as prospect-list flow.
      const rs = r.data?._reveal_status || {};
      if (rs.email_revealed || rs.phone_revealed) {
        const bits = [];
        if (r.data.email) bits.push(`✉ ${r.data.email}`);
        if (r.data.phone) bits.push(`☎ ${r.data.phone}`);
        toast.success(`Enriched — ${bits.join(" · ")}`);
      } else if (rs.email_locked_by_apollo_plan) {
        const alt = (rs.alt_channels || [])[0];
        toast.warning(
          "Apollo has this contact but won't reveal email on your current plan." +
          (alt ? ` Alternative: ${alt.kind} ${alt.url || alt.value}.` : ""),
          { duration: 10000 },
        );
      } else {
        toast.info("No email/phone available for this contact in Apollo.", { duration: 8000 });
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object") {
        toast.error(`${d.message || d.code || "Enrich failed"}${d.hint ? ` — ${d.hint}` : ""}`, { duration: 12000 });
      } else {
        toast.error(String(d || err?.message || "Enrich failed"), { duration: 8000 });
      }
    } finally { setBusy(false); }
  };
  if (shown.email && !enriched) {
    return <span className="text-[10px] text-emerald-700 whitespace-nowrap">✓ ready</span>;
  }
  if (enriched && enriched.email) {
    return <a href={`mailto:${enriched.email}`} className="text-[10px] text-primary hover:underline truncate max-w-[130px] inline-block"
              onClick={(e) => e.stopPropagation()}>{enriched.email}</a>;
  }
  return (
    <button type="button" onClick={doEnrich} disabled={busy}
            className="text-[10px] text-primary hover:underline disabled:opacity-50 whitespace-nowrap"
            data-testid={`apollo-enrich-${idx}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "🔓 Enrich"}
    </button>
  );
}

function CompaniesTable({ rows, selected, setSelected }) {
  const allChecked = rows.length > 0 && rows.every((_, i) => selected[i]);
  const someChecked = rows.some((_, i) => selected[i]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(rows.reduce((acc, _, i) => { acc[i] = true; return acc; }, {}));
  };
  const clean = (v) => v == null || v === "" ? "—" : v;
  const joinList = (arr, max = 3) => Array.isArray(arr) && arr.length
    ? arr.slice(0, max).join(", ") + (arr.length > max ? ` +${arr.length - max}` : "")
    : "—";
  const linkOut = (url) => url
    ? <a href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}><ExternalLink className="w-3 h-3 inline" /></a>
    : <span className="text-muted-foreground">—</span>;

  return (
    <table className="text-[11px] w-full border-collapse" data-testid="apollo-companies-table">
      <thead className="sticky top-0 z-10 bg-muted/70 backdrop-blur">
        <tr>
          <th className="px-2 py-2 text-left border-b border-border w-8 sticky left-0 bg-muted/70">
            <input type="checkbox" checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                    onChange={toggleAll}
                    data-testid="apollo-select-all" />
          </th>
          {["Company", "Industry", "Sub-industry", "Employees", "Revenue", "Funding", "Stage",
            "HQ", "Founded", "Website", "LinkedIn", "Twitter", "Ticker",
            "Technologies", "Description"].map((h) => (
            <th key={h} className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold border-b border-border whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((c, i) => {
          const loc = [c.city, c.state, c.country].filter(Boolean).join(", ");
          return (
            <tr key={c.apollo_id || i}
                className={`border-b border-border hover:bg-muted/20 cursor-pointer ${selected[i] ? "bg-primary/5" : ""}`}
                onClick={() => setSelected((s) => ({ ...s, [i]: !s[i] }))}
                data-testid={`apollo-row-${i}`}>
              <td className="px-2 py-1.5 sticky left-0 bg-card border-r border-border">
                <input type="checkbox" checked={!!selected[i]} readOnly
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setSelected((s) => ({ ...s, [i]: e.target.checked }))}
                        data-testid={`apollo-check-${i}`} />
              </td>
              <td className="px-2 py-1.5 font-semibold whitespace-nowrap max-w-[180px]">
                <div className="flex items-center gap-1">
                  {c.logo_url && <img src={c.logo_url} alt="" className="w-5 h-5 flex-none" />}
                  <div className="min-w-0">
                    <div className="truncate" title={c.name}>{clean(c.name)}</div>
                    {c.domain && <div className="text-[9px] text-muted-foreground">{c.domain}</div>}
                  </div>
                </div>
              </td>
              <td className="px-2 py-1.5 max-w-[130px] truncate" title={c.industry}>{clean(c.industry)}</td>
              <td className="px-2 py-1.5 max-w-[130px] truncate text-muted-foreground" title={c.sub_industry || (c.industries || []).join(", ")}>{clean(c.sub_industry)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{clean(c.employees)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap">{clean(c.revenue)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap">{clean(c.total_funding)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap">{clean(c.latest_funding_stage)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap max-w-[130px] truncate" title={loc}>{clean(loc)}</td>
              <td className="px-2 py-1.5 text-right">{clean(c.founded_year)}</td>
              <td className="px-2 py-1.5 text-center">{linkOut(c.website)}</td>
              <td className="px-2 py-1.5 text-center">{linkOut(c.linkedin_url)}</td>
              <td className="px-2 py-1.5 text-center">{linkOut(c.twitter_url)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap font-mono">{clean(c.publicly_traded_symbol)}</td>
              <td className="px-2 py-1.5 max-w-[180px] truncate" title={(c.technologies || []).join(", ")}>{joinList(c.technologies, 3)}</td>
              <td className="px-2 py-1.5 max-w-[240px] truncate" title={c.description}>{clean(c.description)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}






// ─────────────── Prospects tab ───────────────

function ProspectsTab({ wsId }) {
  const [rows, setRows] = useState(null);
  const [kind, setKind] = useState("_all");
  const [status, setStatus] = useState("_all");
  const [assignee, setAssignee] = useState(""); // R52 · owner filter
  const [q, setQ] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [promoting, setPromoting] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [wsMembers, setWsMembers] = useState([]);
  const [extraUsers, setExtraUsers] = useState({});  // uid → {name, email}
  const [researchProspect, setResearchProspect] = useState(null);  // R60 · Research further dialog

  const load = () => {
    if (!wsId) return;
    const p = new URLSearchParams();
    if (kind !== "_all") p.set("kind", kind);
    if (status !== "_all") p.set("status", status);
    if (assignee) p.set("assignee_id", assignee);
    if (q.trim()) p.set("q", q.trim());
    api.get(`/workspaces/${wsId}/prospects?${p.toString()}`).then((r) => setRows(r.data));
    api.get(`/workspaces/${wsId}/prospects/analytics`).then((r) => setAnalytics(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, kind, status, assignee]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/members`).then((r) => setWsMembers(r.data || [])).catch(() => {});
  }, [wsId]);

  const promote = async (p) => {
    setPromoting((s) => ({ ...s, [p.id]: true }));
    try {
      const target = p.kind === "person" ? "lead" : "client";
      const r = await api.post(`/workspaces/${wsId}/prospects/${p.id}/promote`, { target });
      if (r.data.already_promoted) toast.info(`Already promoted to ${target}`);
      else toast.success(`Promoted to ${target}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Promote failed")); }
    finally { setPromoting((s) => ({ ...s, [p.id]: false })); }
  };

  const idArray = () => Array.from(selectedIds);
  const clearSel = () => setSelectedIds(new Set());

  const bulkPromote = async () => {
    const ids = idArray();
    if (!ids.length) return;
    const r = await api.post(`/workspaces/${wsId}/prospects/bulk-promote`, { ids });
    toast.success(`Promoted ${r.data.promoted} prospect(s)`);
    clearSel();
    load();
  };

  const bulkDelete = async () => {
    const ids = idArray();
    if (!ids.length) return;
    if (!window.confirm(`Soft-delete ${ids.length} prospect(s)? They can be restored from the DB.`)) return;
    try {
      const r = await api.post(`/workspaces/${wsId}/prospects/bulk-delete`, { ids });
      toast.success(`Deleted ${r.data.deleted} prospect(s)`);
      clearSel();
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk delete failed")); }
  };

  const bulkAssign = async (ownerUserId) => {
    const ids = idArray();
    if (!ids.length) return;
    try {
      const r = await api.post(`/workspaces/${wsId}/prospects/bulk-assign`, { ids, owner_user_id: ownerUserId || null });
      toast.success(ownerUserId ? `Assigned ${r.data.updated} prospect(s)` : `Unassigned ${r.data.updated} prospect(s)`);
      setAssignDialogOpen(false);
      clearSel();
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk assign failed")); }
  };

  const bulkTag = async ({ add, remove }) => {
    const ids = idArray();
    if (!ids.length) return;
    try {
      const r = await api.post(`/workspaces/${wsId}/prospects/bulk-tag`, { ids, add, remove });
      toast.success(`Updated tags (added=${r.data.add_count}, removed=${r.data.remove_count})`);
      setTagDialogOpen(false);
      clearSel();
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk tag failed")); }
  };

  // R69d · Bulk-enrich selected prospects — Apollo /people/bulk_match
  // (batched 10-per-request). Only meaningful for person prospects; skips
  // companies. Persists enriched fields back onto each prospect row and
  // refreshes the table.
  const { run: runProspectsBulkEnrich, loading: bulkEnriching, progress: bulkEnrichProgress } = useApolloBulkEnrich(wsId);
  const bulkEnrichProspects = async () => {
    const ids = idArray();
    if (!ids.length) return;
    const selectedRows = (rows || []).filter((r) => ids.includes(r.id) && r.kind === "person");
    if (!selectedRows.length) { toast.error("Bulk enrich works on person prospects"); return; }
    const items = selectedRows.map((p) => ({
      apollo_id: p.apollo_id || null,
      email: p.email || null,
      linkedin_url: p.linkedin_url || null,
      first_name: p.first_name || (p.name || "").split(" ")[0] || null,
      last_name: p.last_name || (p.name || "").split(" ").slice(1).join(" ") || null,
      organization_name: p.company || p.organization_name || null,
      prospect_id: p.id,
    }));
    const data = await runProspectsBulkEnrich(items);
    if (!data) return;
    clearSel();
    load();
  };

  const toCampaign = async () => {
    const ids = idArray();
    if (!ids.length) return;
    const name = window.prompt("Campaign name?", "Cold outreach — scraped list");
    if (!name) return;
    try {
      const r = await api.post(`/workspaces/${wsId}/prospect-to-campaign`, { prospect_ids: ids, name });
      toast.success(`Created segment "${r.data.segment.name}" + AI template + draft campaign "${r.data.campaign.name}" (dry run). Open Marketing → Campaigns to review & send.`, { duration: 8000 });
      clearSel();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name || "(unnamed)"}?`)) return;
    await api.delete(`/workspaces/${wsId}/prospects/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(needle) ||
      (r.company || "").toLowerCase().includes(needle) ||
      (r.email || "").toLowerCase().includes(needle) ||
      (r.domain || "").toLowerCase().includes(needle));
  }, [rows, q]);

  const ownerNameById = useMemo(() => {
    const m = new Map();
    (wsMembers || []).forEach((mem) => {
      m.set(mem.user_id, mem.user?.name || mem.name || mem.email || mem.user_id?.slice(0, 8));
    });
    // Layer in any additional users we resolved separately (e.g. removed members
    // still referenced as prospect owners).
    Object.entries(extraUsers || {}).forEach(([uid, info]) => {
      if (!m.has(uid)) m.set(uid, info.name || info.email || uid.slice(0, 8));
    });
    return m;
  }, [wsMembers, extraUsers]);

  // Iter 137 · fetch names for any owner_user_ids that aren't in wsMembers
  // (removed/soft-deleted members). Prevents the Owner cell from showing UUID[:8].
  useEffect(() => {
    if (!rows || !wsId) return;
    const known = new Set((wsMembers || []).map((m) => m.user_id));
    const missing = Array.from(new Set(
      rows.map((r) => r.owner_user_id).filter((uid) => uid && !known.has(uid) && !extraUsers[uid])
    ));
    if (!missing.length) return;
    api.get(`/workspaces/${wsId}/users/lookup?ids=${missing.join(",")}`)
      .then((r) => {
        const map = {};
        (r.data || []).forEach((u) => { map[u.id] = { name: u.name, email: u.email }; });
        setExtraUsers((prev) => ({ ...prev, ...map }));
      })
      .catch(() => {});
  }, [rows, wsMembers, wsId, extraUsers]);

  const columns = useMemo(() => [
    { key: "identity", label: "Prospect", width: 300, sortable: false, frozen: true,
      render: (p) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <Badge variant="outline" className="rounded-none text-[9px]">{p.kind}</Badge>
            <Badge variant="outline" className="rounded-none text-[9px]">{p.source}</Badge>
            <span className="font-semibold text-xs truncate">{p.name || "(unnamed)"}</span>
            {p.promoted_to && <Badge variant="outline" className="rounded-none text-[9px] border-emerald-500/40 text-emerald-700"><CheckCircle2 className="w-2.5 h-2.5 mr-1" />{p.promoted_to}</Badge>}
          </div>
          {p.kind === "person" ? <PersonRow p={p} wsId={wsId} onEnriched={load} /> : <CompanyRow c={p} />}
        </div>
      ),
      exportValue: (p) => `${p.name || ""}${p.company ? " · " + p.company : ""}`,
    },
    // R56 · Dedicated column for revealed email — highlights enriched rows.
    { key: "email", label: "Email", width: 220, sortable: true, filterType: "text",
      render: (p) => p.email
        ? <span className="text-xs font-mono text-emerald-700 truncate">{p.email}</span>
        : p.personal_emails?.length
          ? <span className="text-xs font-mono text-emerald-700 truncate" title={p.personal_emails.join(", ")}>{p.personal_emails[0]}</span>
          : <span className="text-[10px] text-amber-700 italic">locked</span>,
      exportValue: (p) => p.email || (p.personal_emails || [])[0] || "" },
    { key: "phone", label: "Phone", width: 150, sortable: true, filterType: "text",
      render: (p) => p.phone
        ? <span className="text-xs font-mono truncate">{p.phone}</span>
        : <span className="text-[10px] text-muted-foreground italic">—</span>,
      exportValue: (p) => p.phone || "" },
    { key: "title", label: "Title", width: 180, sortable: true, filterType: "text",
      render: (p) => p.title
        ? <span className="text-xs truncate">{p.title}</span>
        : <span className="text-[10px] text-muted-foreground italic">—</span>,
      exportValue: (p) => p.title || "" },
    { key: "company", label: "Company", width: 180, sortable: true, filterType: "text",
      render: (p) => p.company || p.name && p.kind === "company"
        ? <span className="text-xs truncate">{p.kind === "company" ? p.name : p.company}</span>
        : <span className="text-[10px] text-muted-foreground italic">—</span>,
      exportValue: (p) => (p.kind === "company" ? p.name : p.company) || "" },
    { key: "industry", label: "Industry", width: 160, sortable: true, filterType: "text", groupable: true,
      accessor: (p) => p.company_industry || p.industry || "",
      render: (p) => {
        const v = p.company_industry || p.industry;
        const sub = p.company_sub_industry || p.sub_industry;
        return v
          ? <div className="text-xs truncate">{v}{sub && <span className="text-[10px] text-muted-foreground"> · {sub}</span>}</div>
          : <span className="text-[10px] text-muted-foreground italic">—</span>;
      },
      exportValue: (p) => p.company_industry || p.industry || "" },
    { key: "location", label: "Location", width: 150, sortable: true, filterType: "text",
      accessor: (p) => [p.city, p.state, p.country].filter(Boolean).join(", "),
      render: (p) => {
        const s = [p.city, p.state, p.country].filter(Boolean).join(", ");
        return s ? <span className="text-xs truncate">{s}</span> : <span className="text-[10px] text-muted-foreground italic">—</span>;
      },
      exportValue: (p) => [p.city, p.state, p.country].filter(Boolean).join(", ") },
    { key: "seniority", label: "Seniority", width: 110, sortable: true, filterType: "select", groupable: true,
      filterOptions: ["owner","founder","c_suite","partner","vp","head","director","manager","senior","entry","intern"].map(v => ({ value: v, label: v })),
      render: (p) => p.seniority
        ? <span className="chip text-[10px]">{p.seniority}</span>
        : <span className="text-[10px] text-muted-foreground italic">—</span>,
      exportValue: (p) => p.seniority || "" },
    { key: "department", label: "Dept", width: 120, sortable: true, filterType: "text", groupable: true,
      render: (p) => p.department
        ? <span className="chip text-[10px]">{p.department}</span>
        : <span className="text-[10px] text-muted-foreground italic">—</span>,
      exportValue: (p) => p.department || "" },
    { key: "linkedin_url", label: "LinkedIn", width: 90, sortable: false, filterType: "text",
      accessor: (p) => p.linkedin_url ? "yes" : "",
      render: (p) => p.linkedin_url
        ? <a href={p.linkedin_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[11px] underline hover:no-underline inline-flex items-center gap-1">Open <ExternalLink className="w-2.5 h-2.5" /></a>
        : <span className="text-[10px] text-muted-foreground italic">—</span>,
      exportValue: (p) => p.linkedin_url || "" },
    { key: "kind", label: "Kind", width: 90, sortable: true, groupable: true,
      filterType: "select", filterOptions: [{ value: "person", label: "person" }, { value: "company", label: "company" }],
      render: (p) => <span className="text-xs uppercase">{p.kind}</span>,
      exportValue: (p) => p.kind },
    { key: "source", label: "Source", width: 110, sortable: true, groupable: true,
      filterType: "select", filterOptions: [{ value: "apollo", label: "apollo" }, { value: "web", label: "web" }, { value: "linkedin_salesnav", label: "linkedin" }, { value: "zoominfo", label: "zoominfo" }, { value: "clearbit", label: "clearbit" }],
      render: (p) => <span className="text-xs">{p.source || "—"}</span>,
      exportValue: (p) => p.source },
    { key: "status", label: "Status", width: 100, sortable: true, groupable: true,
      filterType: "select", filterOptions: [{ value: "new", label: "new" }, { value: "promoted", label: "promoted" }],
      render: (p) => <Badge variant="outline" className={`rounded-none text-[9px] ${p.status === "promoted" ? "border-emerald-500/40 text-emerald-700" : ""}`}>{p.status || "new"}</Badge>,
      exportValue: (p) => p.status || "new" },
    { key: "last_enriched_at", label: "Enriched", width: 110, sortable: true, filterType: "date-range",
      accessor: (p) => p.last_enriched_at || "",
      render: (p) => p.last_enriched_at
        ? <span className="text-[10px] text-emerald-700">{(p.last_enriched_at || "").slice(0, 10)}</span>
        : <span className="text-[10px] text-muted-foreground italic">—</span>,
      exportValue: (p) => p.last_enriched_at || "" },
    { key: "owner_user_id", label: "Owner", width: 130, sortable: true, filterType: "text",
      render: (p) => p.owner_user_id
        ? <span className="text-xs truncate">{ownerNameById.get(p.owner_user_id) || p.owner_user_id.slice(0, 8)}</span>
        : <span className="text-[10px] text-muted-foreground italic">unassigned</span>,
      exportValue: (p) => (p.owner_user_id ? (ownerNameById.get(p.owner_user_id) || p.owner_user_id) : "") },
    { key: "tags", label: "Tags", width: 160, sortable: false, filterType: "text",
      accessor: (p) => (p.tags || []).join(", "),
      render: (p) => (
        <div className="flex flex-wrap gap-0.5">
          {(p.tags || []).slice(0, 4).map((t) => (
            <span key={t} className="text-[9px] border border-border px-1 py-0.5">{t}</span>
          ))}
          {(p.tags || []).length > 4 && <span className="text-[9px] text-muted-foreground">+{p.tags.length - 4}</span>}
        </div>
      ),
      exportValue: (p) => (p.tags || []).join("|") },
    { key: "created_at", label: "Added", width: 120, sortable: true, filterType: "date-range",
      accessor: (p) => p.created_at,
      render: (p) => <span className="text-[10px] text-muted-foreground">{(p.created_at || "").slice(0, 10)}</span>,
      exportValue: (p) => p.created_at },
  ], [wsId, ownerNameById]);

  return (
    <div className="space-y-4" data-testid="prospects-tab">
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Total" value={analytics.summary.total} testid="pros-stat-total" />
          <MiniStat label="People" value={analytics.summary.people} testid="pros-stat-people" />
          <MiniStat label="Companies" value={analytics.summary.companies} testid="pros-stat-companies" />
          <MiniStat label="Promoted" value={analytics.summary.promoted} testid="pros-stat-promoted" />
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
          <Input className="pl-9 h-9 rounded-none" placeholder="Search name, company, email, domain…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} data-testid="prospects-search" />
        </div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-9 w-36 rounded-none" data-testid="prospects-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All kinds</SelectItem><SelectItem value="person">People</SelectItem><SelectItem value="company">Companies</SelectItem></SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-36 rounded-none" data-testid="prospects-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_all">All statuses</SelectItem><SelectItem value="new">New</SelectItem><SelectItem value="promoted">Promoted</SelectItem></SelectContent>
        </Select>
        <Select value={assignee || "_all"} onValueChange={(v) => setAssignee(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-9 w-40 rounded-none" data-testid="prospects-owner-filter"><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent className="max-h-[280px]">
            <SelectItem value="_all">All owners</SelectItem>
            <SelectItem value="me">Assigned to me</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {wsMembers.map(m => (
              <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email || m.user_id}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* R69e · Bulk-enrich progress bar (shown for runs > 10 items). */}
      {bulkEnrichProgress && bulkEnrichProgress.total > 10 && (
        <div className="border border-border bg-muted/30 px-3 py-2" data-testid="prospects-bulk-enrich-progress">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="font-medium">Enriching {bulkEnrichProgress.current}/{bulkEnrichProgress.total} prospects · Apollo bulk_match</span>
            <span className="text-muted-foreground">
              {bulkEnrichProgress.emails_revealed} emails · {bulkEnrichProgress.phones_revealed} phones · {bulkEnrichProgress.not_found} not found · {bulkEnrichProgress.errors} errors
            </span>
          </div>
          <div className="h-1.5 bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(bulkEnrichProgress.current / bulkEnrichProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      <AdvancedTable
        tableKey="prospects"
        title="Prospects"        rows={filteredRows}
        columns={columns}
        loading={rows === null}
        emptyIcon={Users}
        emptyTitle={rows && rows.length === 0 ? "No prospects yet" : "No prospects match your filters"}
        emptyDescription="Head to Search and import from Apollo or an AI Web Search."
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        renderBulkBar={() => (
          <>
            <Button size="sm" className="rounded-none h-7" onClick={bulkPromote} data-testid="prospects-bulk-promote">
              <ArrowRight className="w-3 h-3 mr-1" />Promote
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setAssignDialogOpen(true)} data-testid="prospects-bulk-assign">
              <Users className="w-3 h-3 mr-1" />Assign owner
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setTagDialogOpen(true)} data-testid="prospects-bulk-tag">
              <TagIcon className="w-3 h-3 mr-1" />Tags
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-7 border-primary/50 text-primary" onClick={bulkEnrichProspects} disabled={bulkEnriching} data-testid="prospects-bulk-enrich" title="Reveal email + phone for selected people via Apollo bulk_match">
              {bulkEnriching ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}Bulk enrich
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-7 border-violet-500/50 text-violet-600" onClick={toCampaign} data-testid="prospects-to-campaign">
              <Sparkles className="w-3 h-3 mr-1" />To Campaign (AI)
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-7 border-destructive/50 text-destructive" onClick={bulkDelete} data-testid="prospects-bulk-delete">
              <Trash2 className="w-3 h-3 mr-1" />Delete
            </Button>
          </>
        )}
        renderRowActions={(p) => (
          <span className="flex gap-1 justify-end">
            <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-primary"
                    onClick={(e) => { e.stopPropagation(); setResearchProspect(p); }}
                    title="Research further with AI"
                    data-testid={`prospect-research-${p.id}`}>
              <Sparkles className="w-3.5 h-3.5" />
            </Button>
            {!p.promoted_to && (
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" disabled={promoting[p.id]} onClick={(e) => { e.stopPropagation(); promote(p); }} data-testid={`prospect-promote-${p.id}`}>
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive" onClick={(e) => { e.stopPropagation(); del(p); }} data-testid={`prospect-delete-${p.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </span>
        )}
      />

      {researchProspect && (
        <ResearchDialog
          open={!!researchProspect}
          onClose={() => setResearchProspect(null)}
          kind="prospect"
          targetId={researchProspect.id}
          targetName={researchProspect.name}
        />
      )}

      {assignDialogOpen && (
        <BulkAssignDialog
          members={wsMembers}
          count={selectedIds.size}
          onCancel={() => setAssignDialogOpen(false)}
          onAssign={bulkAssign}
        />
      )}
      {tagDialogOpen && (
        <BulkTagDialog
          count={selectedIds.size}
          onCancel={() => setTagDialogOpen(false)}
          onApply={bulkTag}
        />
      )}
    </div>
  );
}

function BulkAssignDialog({ members, count, onCancel, onAssign }) {
  const [ownerId, setOwnerId] = useState("");
  const active = (members || []).filter((m) => (m.status || "active") !== "removed" && (m.status || "active") !== "deleted");
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign owner to {count} prospect{count === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Pick a workspace member to own the selected prospects, or click Unassign to clear ownership.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="label-eyebrow text-[10px]">Owner</Label>
          <Select value={ownerId} onValueChange={setOwnerId}>
            <SelectTrigger className="rounded-none" data-testid="bulk-assign-select"><SelectValue placeholder="— select a workspace member —" /></SelectTrigger>
            <SelectContent>
              {active.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.name || m.email || m.user_id?.slice(0, 8)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">Clear to unassign all selected prospects.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button variant="outline" className="rounded-none" onClick={() => onAssign(null)} data-testid="bulk-assign-clear">Unassign</Button>
          <Button className="rounded-none" disabled={!ownerId} onClick={() => onAssign(ownerId)} data-testid="bulk-assign-apply">Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkTagDialog({ count, onCancel, onApply }) {
  const [addRaw, setAddRaw] = useState("");
  const [removeRaw, setRemoveRaw] = useState("");
  const apply = () => {
    const add = addRaw.split(",").map((s) => s.trim()).filter(Boolean);
    const remove = removeRaw.split(",").map((s) => s.trim()).filter(Boolean);
    if (!add.length && !remove.length) { toast.error("Nothing to change"); return; }
    onApply({ add, remove });
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-tag-dialog">
        <DialogHeader>
          <DialogTitle>Tag {count} prospect{count === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Add or remove comma-separated tags across all selected prospects in one shot.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow text-[10px]">Add tags (comma-separated)</Label>
            <Input value={addRaw} onChange={(e) => setAddRaw(e.target.value)} className="h-9 rounded-none" placeholder="q1-outreach, priority" data-testid="bulk-tag-add" />
          </div>
          <div>
            <Label className="label-eyebrow text-[10px]">Remove tags (comma-separated)</Label>
            <Input value={removeRaw} onChange={(e) => setRemoveRaw(e.target.value)} className="h-9 rounded-none" placeholder="cold, stale" data-testid="bulk-tag-remove" />
          </div>
          <p className="text-[11px] text-muted-foreground">Tags are added via $addToSet (idempotent) and removed via $pullAll. Existing tags on prospects are preserved.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button className="rounded-none" onClick={apply} data-testid="bulk-tag-apply">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <Card className="rounded-none" data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-foreground/55">{label}</div>
        <div className="text-2xl font-bold leading-none mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}


// ─────────────── Sources tab (API key config) ───────────────

function SourcesTab({ wsId }) {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const load = () => api.get(`/workspaces/${wsId}/integrations/apollo`).then((r) => setStatus(r.data));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);

  const save = async () => {
    if (!apiKey.trim() || apiKey.length < 8) { toast.error("Paste a valid Apollo API key"); return; }
    setBusy(true);
    try {
      await api.put(`/workspaces/${wsId}/integrations/apollo`, { api_key: apiKey.trim() });
      toast.success("Apollo key saved (encrypted at rest)");
      setApiKey("");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save")); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/integrations/apollo/test`);
      toast.success(`Apollo OK · ${r.data.total_entries?.toLocaleString?.() ?? "?"} people accessible`);
    } catch (e) { toast.error(apiErrorMessage(e, "Test failed")); }
    finally { setTesting(false); }
  };
  const del = async () => {
    if (!window.confirm("Remove the Apollo key from this workspace?")) return;
    await api.delete(`/workspaces/${wsId}/integrations/apollo`);
    toast.success("Key removed");
    load();
  };
  if (!status) return null;
  return (
    <div className="max-w-2xl space-y-5" data-testid="sources-tab">
      <Card className="rounded-none">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-border flex items-center justify-center bg-orange-50"><Sparkles className="w-5 h-5 text-orange-600" /></div>
            <div className="flex-1">
              <div className="font-bold text-sm">Apollo.io</div>
              <div className="text-xs text-foreground/60">People + Companies search & enrichment.</div>
            </div>
            <Badge variant="outline" className={`rounded-none ${status.configured ? "border-emerald-500/40 text-emerald-700" : "border-foreground/25 text-foreground/60"}`} data-testid="apollo-status-badge">
              {status.configured ? "Configured" : "Not configured"}
            </Badge>
          </div>
          {status.configured && status.updated_at && (
            <div className="text-xs text-foreground/60 border-l-2 border-emerald-500/40 pl-3">
              Configured by {status.updated_by_name || "someone"} · {status.updated_at.slice(0, 16).replace("T", " ")}
            </div>
          )}
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block flex items-center gap-1"><KeyRound className="w-3 h-3" />Apollo API key</Label>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={status.configured ? "•••••• (paste to replace)" : "Paste your Apollo API key"} className="h-9 rounded-none font-mono text-xs" data-testid="apollo-key-input" />
            <p className="text-[11px] text-foreground/55 mt-1">Get one at <a href="https://app.apollo.io/#/settings/integrations/api" target="_blank" rel="noreferrer" className="underline">Apollo Settings → API</a>. Stored encrypted at rest — never shown after save.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={busy} className="rounded-none" data-testid="apollo-key-save"><Save className="w-3 h-3 mr-1" />{busy ? "Saving…" : (status.configured ? "Replace key" : "Save key")}</Button>
            {status.configured && <Button onClick={test} disabled={testing} variant="outline" className="rounded-none" data-testid="apollo-key-test">{testing ? "Testing…" : "Test connection"}</Button>}
            {status.configured && <Button onClick={del} variant="ghost" className="rounded-none text-destructive ml-auto" data-testid="apollo-key-delete"><Trash2 className="w-3 h-3 mr-1" />Remove</Button>}
          </div>
        </CardContent>
      </Card>

      <GenericSourceCard wsId={wsId} source="linkedin_salesnav" />
      <GenericSourceCard wsId={wsId} source="zoominfo" />
      <GenericSourceCard wsId={wsId} source="clearbit" />
      <GenericSourceCard wsId={wsId} source="serper" />

      <Card className="rounded-none border-dashed">
        <CardContent className="p-5 text-xs text-foreground/60">
          <p className="font-semibold text-foreground/80 mb-1">AI Web Search</p>
          <p>Runs a resilient fallback chain: <strong>Serper.dev (recommended)</strong> → Bing → DuckDuckGo. Add a Serper key above so this feature keeps working reliably on production. All sources flow into the unified <strong>Prospects</strong> collection with a <code>source:</code> tag.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────── Multi-source additions (LinkedIn / ZoomInfo / Clearbit / AI Web) ───────────────

function SourceResults({ wsId, items, source, note }) {
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const picked = items.filter((_, i) => selected[i]);
  const importSelected = async () => {
    if (!picked.length) { toast.error("Select at least one row"); return; }
    setImporting(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/prospects/import`, { kind: "person", source, records: picked });
      toast.success(`Imported ${r.data.imported} · Updated ${r.data.updated}`);
      setSelected({});
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setImporting(false); }
  };
  if (!items) return null;
  return (
    <div className="space-y-3 min-w-0">
      <div className="flex items-center justify-between text-sm">
        <span data-testid={`${source}-results-count`}><strong>{items.length}</strong> results{note ? ` · ${note}` : ""}</span>
        <Button size="sm" className="rounded-none" onClick={importSelected} disabled={importing || !picked.length} data-testid={`${source}-import-btn`}>
          <Download className="w-3 h-3 mr-1" />Import {picked.length || ""}
        </Button>
      </div>
      <div className="border border-border" data-testid={`${source}-results`}>
        {items.length === 0 ? <div className="p-6 text-sm text-foreground/55 text-center">No matches — adjust your search.</div>
          : items.map((r, i) => (
            <label key={r.source_id || i} className="flex items-start gap-3 px-3 py-3 border-b border-border last:border-0 hover:bg-foreground/5 cursor-pointer" data-testid={`${source}-row-${i}`}>
              <input type="checkbox" checked={!!selected[i]} onChange={(e) => setSelected((s) => ({ ...s, [i]: e.target.checked }))} className="mt-1" data-testid={`${source}-check-${i}`} />
              <div className="flex-1 min-w-0 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{r.name || r.company || "—"}</span>
                  {r.title && <span className="text-foreground/60 text-xs">{r.title}</span>}
                </div>
                <div className="text-xs text-foreground/60 flex items-center gap-2 flex-wrap mt-0.5">
                  {r.company && r.name !== r.company && <span>{r.company}</span>}
                  {r.company_domain && <span className="font-mono">{r.company_domain}</span>}
                  {r.email && <span className="text-emerald-700">{r.email}</span>}
                  {r.phone && <span>{r.phone}</span>}
                  {r.linkedin_url && <a href={r.linkedin_url} target="_blank" rel="noreferrer" className="underline" onClick={(e) => e.stopPropagation()}>LinkedIn</a>}
                  {[r.city, r.country].filter(Boolean).length > 0 && <span>{[r.city, r.country].filter(Boolean).join(", ")}</span>}
                </div>
                {r.note && <div className="text-[11px] text-violet-700 mt-0.5">{r.note}</div>}
                {r.email_locked_hint && <div className="text-[11px] text-amber-700 mt-0.5">{r.email_locked_hint}</div>}
              </div>
            </label>
          ))}
      </div>
    </div>
  );
}

function WebSearchForm({ wsId }) {
  const [prompt, setPrompt] = useState("");
  const [keywords, setKeywords] = useState("");
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");
  const [titles, setTitles] = useState("");
  const [limit, setLimit] = useState(20);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [presets, setPresets] = useState([]);
  const csv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  useEffect(() => {
    if (wsId) api.get(`/workspaces/${wsId}/web-search-presets`).then((r) => setPresets(r.data || [])).catch(() => {});
  }, [wsId]);
  const savePreset = async () => {
    if (prompt.trim().length < 5) { toast.error("Fill the search prompt first (min 5 chars)"); return; }
    const name = window.prompt("Preset name?", prompt.trim().slice(0, 40));
    if (!name) return;
    try {
      const r = await api.post(`/workspaces/${wsId}/web-search-presets`, {
        name, prompt: prompt.trim(), keywords: csv(keywords), location: location || null,
        industry: industry || null, titles: csv(titles), limit: Number(limit) || 20,
      });
      setPresets((p) => [r.data, ...p]);
      toast.success("Preset saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save preset")); }
  };
  const deletePreset = async (pid) => {
    try {
      await api.delete(`/workspaces/${wsId}/web-search-presets/${pid}`);
      setPresets((p) => p.filter((x) => x.id !== pid));
      toast.success("Preset deleted");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to delete")); }
  };
  const applyPreset = (pid) => {
    const p = presets.find((x) => x.id === pid);
    if (!p) return;
    setPrompt(p.prompt || "");
    setKeywords((p.keywords || []).join(", "));
    setLocation(p.location || "");
    setIndustry(p.industry || "");
    setTitles((p.titles || []).join(", "));
    setLimit(p.limit || 20);
    toast.info(`Preset "${p.name}" applied`);
  };
  const run = async () => {
    if (prompt.trim().length < 5) { toast.error("Describe what you're looking for (min 5 chars)"); return; }
    setBusy(true); setRes(null);
    try {
      const r = await api.post(`/workspaces/${wsId}/prospect-search/web/run`, {
        prompt: prompt.trim(), keywords: csv(keywords), location: location || null,
        industry: industry || null, titles: csv(titles), limit: Number(limit) || 20,
      });
      setRes(r.data);
      if (!r.data.items.length) toast.info("No prospects extracted — try broader keywords");
    } catch (e) { toast.error(apiErrorMessage(e, "Web search failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5" data-testid="web-search">
      <Card className="rounded-none h-fit sticky top-4">
        <CardContent className="p-4 space-y-4">
          {presets.length > 0 && (
            <div data-testid="web-presets">
              <Label className="label-eyebrow mb-1.5 inline-block">Saved presets</Label>
              <div className="space-y-1">
                {presets.map((p) => (
                  <div key={p.id} className="flex items-center gap-1 text-xs">
                    <button onClick={() => applyPreset(p.id)} className="flex-1 text-left border border-border px-2 py-1 hover:border-primary truncate" data-testid={`web-preset-${p.id}`}>{p.name}</button>
                    <button onClick={() => deletePreset(p.id)} className="text-destructive px-1" data-testid={`web-preset-del-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Search prompt *</Label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
              placeholder="e.g. digital marketing agencies in Pune serving SaaS startups"
              className="w-full border border-border p-2 text-sm rounded-none bg-transparent" data-testid="web-prompt" />
          </div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Keywords (comma-separated)</Label>
            <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="agency, performance marketing" className="h-9 rounded-none" data-testid="web-keywords" /></div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Pune, India" className="h-9 rounded-none" data-testid="web-location" /></div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Industry</Label>
            <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="SaaS, fintech…" className="h-9 rounded-none" data-testid="web-industry" /></div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Titles / roles</Label>
            <Input value={titles} onChange={(e) => setTitles(e.target.value)} placeholder="Founder, CMO" className="h-9 rounded-none" data-testid="web-titles" /></div>
          <div><Label className="label-eyebrow mb-1.5 inline-block">Max results</Label>
            <Input type="number" min={1} max={50} value={limit} onChange={(e) => setLimit(e.target.value)} className="h-9 rounded-none w-24" data-testid="web-limit" /></div>
          <Button className="w-full rounded-none" onClick={run} disabled={busy} data-testid="web-run">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning the web…</> : <><SearchIcon className="w-4 h-4 mr-2" />Search the web (AI)</>}
          </Button>
          <Button variant="outline" className="w-full rounded-none" onClick={savePreset} data-testid="web-save-preset"><Save className="w-3 h-3 mr-2" />Save as preset</Button>
          <p className="text-[11px] text-foreground/55">Runs Serper.dev → Bing → DuckDuckGo, then extracts prospects with AI. Add a Serper key in <strong>Sources</strong> for reliable results. Takes ~20-40s.</p>
        </CardContent>
      </Card>
      {res
        ? <SourceResults wsId={wsId} items={res.items} source="web" note={`${res.pages_scanned} pages scanned`} />
        : <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60" data-testid="web-empty">Describe your ideal prospects and hit <strong>Search the web</strong>. AI scans real pages and extracts contacts.</CardContent></Card>}
    </div>
  );
}

const PROVIDER_HINTS = {
  linkedin_salesnav: { keyLabel: "Proxycurl API key", hint: "Sales-Nav-style people search via Proxycurl (proxycurl.com). Needs company/title/location filters.", fields: ["query", "titles", "company", "location", "country"] },
  zoominfo: { keyLabel: "ZoomInfo PAT / JWT", hint: "Contact search via ZoomInfo API. Provide a valid access token.", fields: ["query", "titles", "company", "location"] },
  clearbit: { keyLabel: "Clearbit secret key", hint: "Prospector API — requires a company domain.", fields: ["domain", "titles", "query"] },
  serper: { keyLabel: "Serper.dev API key", hint: "Google Search API used by AI Web Search. Free tier: 2,500 queries/month at serper.dev. Highly recommended so AI Web Search doesn't rely on DuckDuckGo scraping.", fields: [] },
};

function ProviderSearchForm({ wsId, source, configured }) {
  const [f, setF] = useState({ query: "", domain: "", company: "", titles: "", location: "", country: "" });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const meta = PROVIDER_HINTS[source];
  const csv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  const run = async () => {
    setBusy(true); setRes(null);
    try {
      const r = await api.post(`/workspaces/${wsId}/prospect-search/${source}`, {
        kind: "people", query: f.query || null, domain: f.domain || null, company: f.company || null,
        titles: csv(f.titles), location: f.location || null, country: f.country || null, per_page: 25,
      });
      setRes(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Search failed")); }
    finally { setBusy(false); }
  };
  if (!configured) {
    return (
      <Card className="rounded-none border-amber-500/50" data-testid={`${source}-not-configured`}>
        <CardContent className="p-6 space-y-2">
          <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5 text-amber-600" /><h3 className="font-bold">{meta.keyLabel} not configured</h3></div>
          <p className="text-sm text-foreground/70">Add it in the <strong>Sources</strong> tab. {meta.hint}</p>
        </CardContent>
      </Card>
    );
  }
  const FIELD_META = { query: "Keywords / name", domain: "Company domain *", company: "Company name", titles: "Titles (comma-separated)", location: "City / location", country: "Country" };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5" data-testid={`${source}-search`}>
      <Card className="rounded-none h-fit sticky top-4">
        <CardContent className="p-4 space-y-4">
          {meta.fields.map((k) => (
            <div key={k}><Label className="label-eyebrow mb-1.5 inline-block">{FIELD_META[k]}</Label>
              <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-9 rounded-none" data-testid={`${source}-${k}`} /></div>
          ))}
          <Button className="w-full rounded-none" onClick={run} disabled={busy} data-testid={`${source}-run`}>
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching…</> : <><SearchIcon className="w-4 h-4 mr-2" />Search</>}
          </Button>
          <p className="text-[11px] text-foreground/55">{meta.hint}</p>
        </CardContent>
      </Card>
      {res
        ? <SourceResults wsId={wsId} items={res.items} source={source} />
        : <Card className="rounded-none border-dashed"><CardContent className="p-10 text-center text-sm text-foreground/60">Set filters and hit <strong>Search</strong>.</CardContent></Card>}
    </div>
  );
}

function GenericSourceCard({ wsId, source, onSaved }) {
  const meta = PROVIDER_HINTS[source];
  const labels = { linkedin_salesnav: "LinkedIn Sales Nav (Proxycurl)", zoominfo: "ZoomInfo", clearbit: "Clearbit Prospector", serper: "Serper.dev (Google Search API)" };
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(false);
  const load = () => api.get(`/workspaces/${wsId}/prospect-sources`).then((r) => {
    setConfigured(!!(r.data.find((s) => s.source === source) || {}).configured);
  }).catch(() => {});
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  const save = async () => {
    if (key.trim().length < 8) { toast.error("Paste a valid API key"); return; }
    setBusy(true);
    try { await api.put(`/workspaces/${wsId}/integrations/${source}`, { api_key: key.trim() }); toast.success(`${labels[source]} key saved`); setKey(""); load(); onSaved?.(); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Remove this key?")) return;
    await api.delete(`/workspaces/${wsId}/integrations/${source}`);
    toast.success("Key removed"); load();
  };
  return (
    <Card className="rounded-none" data-testid={`source-card-${source}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="font-bold text-sm">{labels[source]}</div>
            <div className="text-xs text-foreground/60">{meta.hint}</div>
          </div>
          <Badge variant="outline" className={`rounded-none ${configured ? "border-emerald-500/40 text-emerald-700" : "border-foreground/25 text-foreground/60"}`} data-testid={`${source}-status-badge`}>
            {configured ? "Configured" : "Not configured"}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={configured ? "•••••• (paste to replace)" : `Paste ${meta.keyLabel}`} className="h-9 rounded-none font-mono text-xs" data-testid={`${source}-key-input`} />
          <Button onClick={save} disabled={busy} className="rounded-none shrink-0" data-testid={`${source}-key-save`}>{busy ? "Saving…" : "Save"}</Button>
          {configured && <Button onClick={del} variant="ghost" className="rounded-none text-destructive shrink-0" data-testid={`${source}-key-delete`}><Trash2 className="w-3 h-3" /></Button>}
        </div>
      </CardContent>
    </Card>
  );
}
